// Cart Component - Slide-out cart drawer
import React from 'react';

// Safe number formatting
function formatPrice(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
}

// Get display name for order type
function getOrderTypeName(orderType) {
  if (!orderType) return 'Order';
  return orderType.name || orderType.order_type || 'Order';
}

// Get icon for order type
function getOrderTypeIcon(orderType) {
  const type = orderType?._onlineType || '';
  switch (type) {
    case 'takeout':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
          <line x1="3" y1="6" x2="21" y2="6"/>
          <path d="M16 10a4 4 0 0 1-8 0"/>
        </svg>
      );
    case 'delivery':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="1" y="3" width="15" height="13"/>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
          <circle cx="5.5" cy="18.5" r="2.5"/>
          <circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      );
    case 'dinein':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8h1a4 4 0 0 1 0 8h-1"/>
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/>
          <line x1="6" y1="1" x2="6" y2="4"/>
          <line x1="10" y1="1" x2="10" y2="4"/>
          <line x1="14" y1="1" x2="14" y2="4"/>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      );
  }
}

export function Cart({ 
  open, 
  cart, 
  totals, 
  orderType,
  availableOrderTypes = [],
  deliveryAddress,
  onOrderTypeChange,
  onDeliveryAddressChange,
  onClose, 
  onUpdateItem, 
  onRemoveItem,
  onEditItem,
  onClearCart, 
  onCheckout,
  settings 
}) {
  if (!open) return null;

  const showOrderTypeSelector = availableOrderTypes.length > 1;
  const showOrderTypeBadge = availableOrderTypes.length === 1 && orderType;
  const isDelivery = orderType?._onlineType === 'delivery';

  return (
    <>
      <div className="cart-overlay" onClick={onClose} />
      <div className={`cart-drawer ${open ? 'cart-drawer--open' : ''}`}>
        <div className="cart-drawer__header">
          <h2 className="cart-drawer__title">Your Order</h2>
          <button className="cart-drawer__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="cart-drawer__empty">
            <svg className="cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            <p>Your cart is empty</p>
            <button className="cart-drawer__browse-btn" onClick={onClose}>
              Browse Menu
            </button>
          </div>
        ) : (
          <>
            {/* Order Type Selector or Badge - only show if there are available types */}
            {(showOrderTypeSelector || showOrderTypeBadge) && (
              <div className="cart-drawer__order-type">
                {showOrderTypeSelector ? (
                  <div className="order-type-selector">
                    {availableOrderTypes.map((ot) => {
                      const isActive = (ot.id || ot.ordertypeid) === (orderType?.id || orderType?.ordertypeid);
                      return (
                        <button
                          key={ot.id || ot.ordertypeid}
                          className={`order-type-selector__btn ${isActive ? 'order-type-selector__btn--active' : ''}`}
                          onClick={() => onOrderTypeChange?.(ot)}
                        >
                          <span className="order-type-selector__icon">
                            {getOrderTypeIcon(ot)}
                          </span>
                          <span className="order-type-selector__label">
                            {getOrderTypeName(ot)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <span className="cart-drawer__order-type-badge">
                    {getOrderTypeName(orderType)}
                  </span>
                )}
              </div>
            )}

            {/* Delivery Address Input - show when delivery is selected */}
            {isDelivery && (
              <div className="cart-drawer__delivery-address">
                <label className="delivery-address__label">Delivery Address</label>
                <input
                  type="text"
                  className="delivery-address__input"
                  placeholder="Enter your delivery address"
                  value={deliveryAddress || ''}
                  onChange={(e) => onDeliveryAddressChange?.(e.target.value)}
                />
              </div>
            )}

            {/* Cart Items */}
            <div className="cart-drawer__items">
              {cart.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={(qty) => onUpdateItem(item.id, { quantity: qty })}
                  onRemove={() => onRemoveItem(item.id)}
                  onEdit={() => onEditItem?.(item)}
                />
              ))}
            </div>

            {/* Totals */}
            <div className="cart-drawer__totals">
              <div className="cart-drawer__total-row">
                <span>Subtotal</span>
                <span>${formatPrice(totals.subtotal)}</span>
              </div>
              <div className="cart-drawer__total-row">
                <span>Tax</span>
                <span>${formatPrice(totals.tax)}</span>
              </div>
              {totals.discount > 0 && (
                <div className="cart-drawer__total-row cart-drawer__total-row--discount">
                  <span>Discount</span>
                  <span>-${formatPrice(totals.discount)}</span>
                </div>
              )}
              <div className="cart-drawer__total-row cart-drawer__total-row--total">
                <span>Total</span>
                <span>${formatPrice(totals.total)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="cart-drawer__actions">
              <button 
                className="cart-drawer__checkout-btn"
                onClick={onCheckout}
              >
                Checkout • ${formatPrice(totals.total)}
              </button>
              <button 
                className="cart-drawer__clear-btn"
                onClick={onClearCart}
              >
                Clear Cart
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// Individual cart item
function CartItem({ item, onUpdateQuantity, onRemove, onEdit }) {
  return (
    <div className="cart-item">
      <div className="cart-item__info">
        <h4 className="cart-item__name">{item.name}</h4>
        {item.modifiers && item.modifiers.length > 0 && (
          <div className="cart-item__mods">
            {item.modifiers.map((mod, i) => {
              const modPrice = parseFloat(mod.price) || 0;
              return (
                <span key={i} className="cart-item__mod">
                  {mod.name || mod.option_name}
                  {modPrice > 0 && (
                    <span className="cart-item__mod-price"> +${formatPrice(modPrice)}</span>
                  )}
                </span>
              );
            })}
          </div>
        )}
        {item.specialInstructions && (
          <p className="cart-item__instructions">{item.specialInstructions}</p>
        )}
        <span className="cart-item__price">${formatPrice(item.lineTotal)}</span>
      </div>

      <div className="cart-item__controls">
        {/* Edit button */}
        {item._editMeta && (
          <button 
            className="cart-item__edit-btn"
            onClick={() => onEdit?.(item)}
            title="Edit item"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        )}
        
        <div className="cart-item__quantity">
          <button 
            className="cart-item__qty-btn"
            onClick={() => onUpdateQuantity(Math.max(0, item.quantity - 1))}
          >
            {item.quantity === 1 ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            )}
          </button>
          <span className="cart-item__qty-value">{item.quantity}</span>
          <button 
            className="cart-item__qty-btn"
            onClick={() => onUpdateQuantity(item.quantity + 1)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Cart;
